import * as React from 'react'
import t from './strings'
import { Select } from '@doordash/component-fields'
import Button from '@doordash/component-button'
import { useState } from 'react'
import { CAR_COLORS, CAR_MAKES } from '../../../constants'
import { StackChildren } from '@doordash/design-language'
import {
  arrayToOptions,
  modelOptions,
  processVehicleTypes,
  VEHICLE_OTHER,
  vehicleDetailsMap,
} from '../../../utilities/vehicle'
import Form from '../../../utilities/Form'
import { validators as sharedValidators } from '../../../utilities/validators'
import InsuranceCheckBox from '../../atoms/insurance-checkbox'
import VehicleTypeDropdown from '../../atoms/vehicle-type-dropdown'
import { BackgroundCheck as Tag } from '../../../models/selectorAnchors'
import { connect } from 'react-redux'

export type VehicleType = {
  name: string
  needs_license_info: boolean
}

export type VehicleDetailsSubmitData = {
  vehicleType: string
  make?: string
  model?: string
  color?: string
  insuranceCheck?: boolean
}

type Props = {
  vehicleTypes?: { [key: string]: VehicleType }
  onSubmit: (data: VehicleDetailsSubmitData) => Promise<void>
  requireAdditionalCarInfo?: boolean
}

const defaultFields = {
  vehicleType: null,
  make: '',
  model: '',
  color: '',
  insuranceCheck: '',
}

const baseValidator = {
  vehicleType: sharedValidators.vehicleType,
  make: sharedValidators.hidden,
  model: sharedValidators.hidden,
  color: sharedValidators.hidden,
  insuranceCheck: sharedValidators.hidden,
}

const validatorForCarWithAdditionalInfo = {
  vehicleType: sharedValidators.vehicleType,
  insuranceCheck: sharedValidators.insuranceCheck,
  ...sharedValidators.vehicle,
}

const VehicleDetailsForm: React.FC<Props> = props => {
  const { vehicleTypes, onSubmit, requireAdditionalCarInfo = false } = props
  const [vehicleNeedsLicenseInfo, setVehicleNeedsLicenseInfo] = useState<
    boolean
  >(false)

  const handleVehicleChange = (name, handleChange) => selectedId => {
    handleChange(name, selectedId)
    setVehicleNeedsLicenseInfo(
      Boolean(vehicleTypes[selectedId].needs_license_info)
    )
  }

  const showAdditionalCarInfo =
    requireAdditionalCarInfo && vehicleNeedsLicenseInfo

  return (
    <Form
      onSubmit={onSubmit}
      fields={defaultFields}
      validators={
        showAdditionalCarInfo
          ? validatorForCarWithAdditionalInfo
          : baseValidator
      }
    >
      {(fields, handleChange, handleSubmit, { isLoading }) => (
        <form onSubmit={handleSubmit}>
          <StackChildren size={StackChildren.Sizes.Large}>
            <div data-anchor-id={Tag.VehicleSelect}>
              <VehicleTypeDropdown
                accessibilityDescribedBy={'vehicleType'}
                label={t.vehicleTypeLabel()}
                description={t.required()}
                vehicleTypes={processVehicleTypes(vehicleTypes)}
                onChange={handleVehicleChange('vehicleType', handleChange)}
                {...fields.vehicleType}
              />
            </div>
            {showAdditionalCarInfo && (
              <>
                <Select
                  accessibilityDescribedBy={'carMake'}
                  label={t.makeLabel()}
                  description={t.required()}
                  onChange={val => {
                    handleChange('make', val)
                    if (val === VEHICLE_OTHER) {
                      handleChange('model', VEHICLE_OTHER)
                    }
                  }}
                  options={arrayToOptions(
                    ['', ...CAR_MAKES, VEHICLE_OTHER],
                    vehicleDetailsMap
                  )}
                  {...fields.make}
                />
                <Select
                  accessibilityDescribedBy={'carModel'}
                  label={t.modelLabel()}
                  description={t.required()}
                  disabled={fields.make.value !== ''}
                  onChange={handleChange.bind(this, 'model')}
                  options={modelOptions(fields.make.value)}
                  {...fields.model}
                />
                <Select
                  accessibilityDescribedBy={'carColor'}
                  label={t.colorLabel()}
                  description={t.required()}
                  onChange={handleChange.bind(this, 'color')}
                  options={['', ...arrayToOptions(CAR_COLORS, t.colors)]}
                  {...fields.color}
                />
                <InsuranceCheckBox
                  id="inset-insurance"
                  accessibilityDescribedBy={'insuranceCheckbox'}
                  label={t.insuranceCertificationLabel()}
                  onChange={handleChange.bind(this, 'insuranceCheck')}
                  anchorId={Tag.InsuranceCheckbox}
                  {...fields.insuranceCheck}
                />
              </>
            )}
            <Button
              accessibilityDescribedBy={'submitVehicleDetail'}
              onClick={async () => {
                // TODO: add tracking events for submission
                await handleSubmit()
              }}
              state={isLoading ? Button.States.Loading : Button.States.Default}
              data-anchor-id={Tag.PrimaryAction}
            >
              {t.continue()}
            </Button>
          </StackChildren>
        </form>
      )}
    </Form>
  )
}

const mapStateToProps = state => ({
  vehicleTypes: state.vehicleTypes,
})

export default connect(mapStateToProps, null)(VehicleDetailsForm)
